import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const PurchasePaymentEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/purchase-payments",
    token
  );
  const [selectedShop, setSelectedShop] = useState("");

  const { data: purchasesData, loading: purchaseLoading } = useFetchObjects(
    ["purchase", "purchase-payment-edit", selectedShop],
    selectedShop ? `shop/purchases/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "purchase-payment-edit", selectedShop],
    selectedShop ? `shop/currencies/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: shopsData, isLoading: shopLoading } = useFetchObjects(
    ["shop", "purchase-payment-edit"],
    "shop/shop",
    token
  );

  const purchases = Array.isArray(purchasesData) ? purchasesData : purchasesData?.results || [];
  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
  );

  const [selectedPurchase, setSelectedPurchase] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentDate, setPaymentDate] = useState(""); // Add payment date state
  const [notes, setNotes] = useState(""); // Add notes state
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const initialRateSelectionRef = useRef("");
  const selectedShopId =
    selectedShop || purchaseDetails?.shop_details?.id || editData?.purchase_info?.shop_id || "";
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "purchase-payment-edit", selectedShopId],
    selectedShopId ? `shop/accounts/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const shopAccounts = accounts;
  const sameCurrencyAccounts = accounts.filter(
    (acc) => String(acc.currency) === String(selectedCurrency)
  );
  const selectedAccountDetails = accounts.find(
    (acc) => String(acc.id) === String(selectedAccount)
  );
  const selectedCurrencyDetails =
    currencies.find((curr) => String(curr.id) === String(selectedCurrency)) ||
    purchaseDetails?.currency_details ||
    null;

  const { data: directRateData } = useFetchObjects(
    [
      "purchase-payment-edit-rate-direct",
      selectedShopId,
      selectedCurrencyDetails?.id,
      selectedAccountDetails?.currency,
    ],
    selectedShopId && selectedCurrencyDetails?.id && selectedAccountDetails?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrencyDetails.id}&to_currency=${selectedAccountDetails.currency}`
      : "",
    token
  );

  const { data: reverseRateData } = useFetchObjects(
    [
      "purchase-payment-edit-rate-reverse",
      selectedShopId,
      selectedCurrencyDetails?.id,
      selectedAccountDetails?.currency,
    ],
    selectedShopId && selectedCurrencyDetails?.id && selectedAccountDetails?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccountDetails.currency}&to_currency=${selectedCurrencyDetails.id}`
      : "",
    token
  );

  useEffect(() => {
    if (editData) {
      setAmount(editData.amount);
      setExchangeRate(editData.exchange_rate || "");
      setPaymentDate(editData.payment_date.slice(0, 16)); // Set payment date to match the format
      setNotes(editData.notes || ""); // Set notes from editData
      const purchaseId =
        editData.purchase && editData.purchase.id
          ? String(editData.purchase.id)
          : editData.purchase
          ? String(editData.purchase)
          : "";
      setSelectedPurchase(purchaseId);
      const shopId =
        editData.purchase_info?.shop_id || editData.shop || "";
      setSelectedShop(String(shopId));

      const currencyId =
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : "";
      setSelectedCurrency(currencyId);
      const accountId =
        editData.account_details && editData.account_details.id
          ? String(editData.account_details.id)
          : editData.account
            ? String(editData.account)
            : "";
      setSelectedAccount(accountId);
      initialRateSelectionRef.current = `${currencyId}:${accountId}`;
    }
  }, [editData]);

  useEffect(() => {
    if (purchases && selectedPurchase) {
      const selectedPurchaseDetails = purchases.find(
        (purchase) => String(purchase.id) === String(selectedPurchase)
      );
      if (selectedPurchaseDetails) {
        setPurchaseDetails(selectedPurchaseDetails);
        setSelectedCurrency(selectedPurchaseDetails.currency_details?.id || "");
      }
    }
  }, [selectedPurchase, purchases]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedPurchase("");
      setSelectedCurrency("");
      setSelectedShop("");
      setAmount("");
      setExchangeRate("");
      setSelectedAccount("");
      setPaymentDate(""); // Reset payment date
      setNotes(""); // Reset notes
      onClose();
    }
  }, [isSuccess, onClose]);

  useEffect(() => {
    if (!selectedCurrency) {
      setSelectedAccount("");
      return;
    }
    if (shopAccounts.length === 0) {
      setSelectedAccount("");
      return;
    }
    const exists = shopAccounts.some((acc) => String(acc.id) === String(selectedAccount));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || shopAccounts[0];
      setSelectedAccount(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, selectedAccount, shopAccounts, sameCurrencyAccounts]);

  const exchangeRateFromSystem = (() => {
    if (!selectedCurrencyDetails || !selectedAccountDetails) return "";
    if (String(selectedCurrencyDetails.id) === String(selectedAccountDetails.currency)) {
      return "1";
    }
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  })();

  useEffect(() => {
    if (!editData || !exchangeRateFromSystem) return;
    const currentRateSelection = `${selectedCurrency}:${selectedAccount}`;
    if (currentRateSelection !== initialRateSelectionRef.current) {
      setExchangeRate(exchangeRateFromSystem);
    }
  }, [exchangeRateFromSystem, editData, selectedCurrency, selectedAccount]);

  const convertedAmount = (() => {
    if (!amount || !exchangeRate) return "";
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  })();

  const handleSubmit = () => {
    const payload = {
      purchase: selectedPurchase,
      currency: selectedCurrency,
      amount,
      exchange_rate: exchangeRate,
      payment_date: paymentDate, // Include payment date
      notes: notes, // Include notes
    };
    if (selectedAccount) payload.account = selectedAccount;
    handleUpdate(editData.id, payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("purchasePayment.editPurchasePayment")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectShop", "Select branch")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => {
              setSelectedShop(e.target.value);
              setSelectedPurchase("");
              setSelectedCurrency("");
              setSelectedAccount("");
              setPurchaseDetails(null);
            }}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectPurchase")}
            fullWidth
            required
            value={selectedPurchase}
            onChange={(e) => setSelectedPurchase(e.target.value)}
            disabled={!selectedShop}
          >
            {purchaseLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (purchases || []).map((purchase) => (
                <MenuItem key={purchase.id} value={purchase.id}>
                  <strong>Purchase #{purchase.id}</strong> - {purchase.supplier_details?.name || 'N/A'}
                </MenuItem>
              ))
            )}
          </TextField>

          {purchaseDetails && (
            <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                <strong>{t("purchasePayment.totalAmount")}:</strong>{" "}
                {purchaseDetails.total_price?.[purchaseDetails.currency_details?.title] || 0}{" "}
                {purchaseDetails.currency_details?.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={1}>
                <strong>{t("purchasePayment.totalPaid")}:</strong>{" "}
                {purchaseDetails.total_paid?.[purchaseDetails.currency_details?.title] || 0}{" "}
                {purchaseDetails.currency_details?.title}
              </Typography>
              <Typography variant="body2" color="error" fontWeight="bold">
                <strong>{t("purchasePayment.totalRemaining")}:</strong>{" "}
                {purchaseDetails.total_remained_amount?.[purchaseDetails.currency_details?.title] || 0}{" "}
                {purchaseDetails.currency_details?.title}
              </Typography>
            </Box>
          )}

          <TextField
            margin="dense"
            label={t("purchasePayment.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((curr) => (
                <MenuItem key={curr.id} value={curr.id}>
                  {curr.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.account", "Account")}
            fullWidth
            required
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("purchasePayment.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("purchasePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          {!exchangeRateFromSystem && selectedPurchase && selectedAccount && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("purchasePayment.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}

          <TextField
            margin="dense"
            label={t("purchasePayment.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmount}
            disabled
          />

          {/* Payment Date Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} // Handle date change
          />

          {/* Notes Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.notes")}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchasePayment.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedPurchase || !selectedCurrency || !amount || !exchangeRate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchasePayment.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePaymentEdit;
